import { useCallback, useEffect, useRef, useState } from "react";
import { getMediaDuration, MediaDurationStatus } from "@/lib/mediaDuration";

interface UseMediaDurationResult {
  status: MediaDurationStatus;
  durationSeconds: number | null;
  retry: () => void;
}

export const useMediaDuration = (file: File | null): UseMediaDurationResult => {
  const [status, setStatus] = useState<MediaDurationStatus>("idle");
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [requestId, setRequestId] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const retry = useCallback(() => {
    if (file) {
      setRequestId((current) => current + 1);
    }
  }, [file]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    setDurationSeconds(null);

    if (!file) {
      setStatus("idle");
      return () => {
        abortControllerRef.current?.abort();
      };
    }

    setStatus("checking");
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let isMounted = true;

    getMediaDuration(file, { signal: controller.signal })
      .then((duration) => {
        if (!isMounted || controller.signal.aborted) return;

        if (duration === null) {
          setStatus("failed");
          setDurationSeconds(null);
        } else {
          setStatus("ready");
          setDurationSeconds(duration);
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        setStatus("failed");
        setDurationSeconds(null);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [file, requestId]);

  return {
    status,
    durationSeconds,
    retry,
  };
};
